import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Router } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { GridsterItem } from 'angular-gridster2';

import { RGBAColor } from '@inmoment/the-kitchen/src/lib/Pickers/ColorPicker/ColorPicker.model';

import { DataSourceType } from 'app/models/widget-data-source.model';
import {
  AnalyticItemFilterSets,
  Filter,
  FilterSet,
} from 'app/modules/collection/services/filter.service';
import {
  ComparisonDateType,
  ComparisonSubType,
  ComparisonType,
} from 'app/modules/dashboard/components/create-comparison-drawer/create-comparison-drawer.component';
import { Breakdown, Driver } from 'app/modules/widget/models/predictive.models';
import { BridgeAPIService, BridgeListResult } from 'app/providers/bridge-api.service';
import { DisableMethodTelemetry, EnableClassTelemetry } from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { ScatterPlotUtils } from 'app/utils/scatter-plot-utils';
import { RealtimeConnection, RealtimeEventHandler, RealtimeService } from '../realtime.service';

import { GroupService } from '../semantria-api/group.service';
import { SectionMetadata, SectionMetadataType } from '../storage-api/collection.service';
import { FieldService } from '../storage-api/field.service';
import { ObjectLabel, Sideloadable } from '../storage-api/object-label.service';
import { urlFor } from '../storage-api/utils';

// eslint-disable-next-line
interface WidgetPayload {}

export type DashboardType = 'traditional' | 'predictive' | 'survey' | string;

export interface WidgetDefault extends WidgetPayload {
  [key: string]: any;
  thumbnail?: string;
  refresh?: (...args) => void;
  comments?: Comment[];
  commentsHidden?: boolean;
  sectionFilter?: SectionFilter[];
}

export interface Widget<T extends WidgetPayload = WidgetDefault> extends GridsterItem {
  id: string;
  component: string;
  title: string;
  payload: T;
  size?: number[];
  dataSourceId?: string;
  properties: {
    is_locked: boolean;
    as_data_table: boolean;
    as_document_list: boolean;
    [key: string]: any;
  };
}

export type CommentType = 'smart-summary' | 'custom';

export type CommentPoint = Omit<CommentTarget, 'analyticItems'>;

export interface Comment {
  id: string;
  title?: string;
  titlePinned?: boolean;
  summary?: string;
  summaryPinned?: boolean;
  content?: string;
  alwaysVisible: boolean;
  type: CommentType;
  smartSummaryId?: string;
  payload?: { [key: string]: any };
  target: CommentTarget;
  createdAt: Date;
  modifiedAt?: Date;
  createdBy: string;
}

export interface CommentTarget {
  analyticItems: {
    name: string;
    displayName: string;
    type: string;
    subType?: string;
  }[];
  point: Highcharts.AnnotationMockPointOptionsObject;
  pointId?: string;
  series?: string;
  column?: string;
  subjectId?: string;
}

export interface Comparison {
  id: string;
  title: string;
  type: ComparisonType;
  color: string;
  filters: ComparisonFilterSet[];
  lastUsedDataSource?: string;
  lastUsedDataField?: string;
  page: number;
  widgets: string[];
}

export interface ComparisonFilterSet extends FilterSet {
  color?: string;
  dynamicDate?: { rangeNumber: number; rangeType: string };
  subType?: ComparisonSubType;
  subDateType?: ComparisonDateType;
}

export interface ComparisonFilterFieldExtra {
  type: string;
  sectionMetadata?: SectionMetadata[];
}

export interface WidgetGroup {
  id: string;
  title: string;
  filters: AnalyticItemFilterSets;
  parentFilters: AnalyticItemFilterSets;
  widgets: string[];
  parentWidget?: string;
  version: number;
  deletable: boolean;
  editable: boolean;
  isImpact: boolean;
  color?: RGBAColor;
  lastUsedDataSource?: string;
  lastUsedDataSourceDisplayName?: string;
  filterCount?: number;
  andFilters?: Filter[];
  orFilters?: Filter[];
  notFilters?: Filter[];
  parameters?: {
    scoreMetric?: string;
    previousStart?: string;
    previousEnd?: string;
    currentStart?: string;
    currentEnd?: string;
    scoringStrategy?: 'NPS' | 'BOX' | 'AVERAGE';
    dependentField?: string;
    breakdownColumn?: string;
    selectedBreakdown?: Breakdown;
    selectedDriver?: Driver;
    driverAnalysisId?: string;
    dependentRange?: string;
    driversRange?: string;
    boxSize?: '1' | '2' | '3' | '4' | '5' | '6' | '7';
    boxDirection?: 'TOP' | 'BOTTOM';
    [key: string]: any;
  };
  page: number;
  isDashboardLevel?: boolean;
}

export const DASHBOARD_LEVEL_WIDGET_GROUP_ID = '--dashboard--';

export interface DashboardPage {
  title: string;
  id: number;
  widgetDetailsHidden?: boolean;
}

export interface ActionPlanSidePaneData {
  id: string;
  page: number;
}

export interface Pin {
  analyticItem: string;
  title: string;
  type?: string;
}

type ContentChannel = string;
type DateKind = 'created' | 'modified';

interface DataSourceBaseFilter {
  readonly id: string;
  readonly label: string;
}

export type DataSourceFilter =
  | (DataSourceBaseFilter & {
      type: 'contentChannel';
      value: ContentChannel;
    })
  | (DataSourceBaseFilter & {
      type: 'dataSourceType';
      value: DataSourceType;
    })
  | (DataSourceBaseFilter & {
      type: 'date';
      dateKind: DateKind;
      //date value
      value: string;
      displayValue: string;
    })
  | (DataSourceBaseFilter & {
      type: 'specificDataSource';
      dataSourceType: DataSourceType;
      //ds id
      value: string;
    })
  | (DataSourceBaseFilter & {
      type: 'label';
      value: string;
    });

export interface DataSourceFilterSet {
  and: DataSourceFilter[];
  or: DataSourceFilter[];
  not: DataSourceFilter[];
}

export type DataSourceFilterType =
  | 'contentChannel'
  | 'dataSourceType'
  | 'specificDataSource'
  | 'date'
  | 'label';

export type DashboardVisibility = 'UNLISTED' | 'PUBLIC';
export interface DashboardPayload {
  analysisName?: string;
  collectionName?: string;
  lastDataSourceId: string;
  dataSourceFilters?: DataSourceFilterSet;
  lastKeyDriverDataSourceId?: string;
  notes: string;
  sentimentScale: number;
  sentimentDisplay: string;
  isExecutive?: boolean;
  type: DashboardType;
  pages: DashboardPage[];
  widgetGroupPanelIsOpened?: boolean;
  timezone: string;
  timezoneOffset: string;
  actionPlans: ActionPlanSidePaneData[];
  pins: Pin[];
}

//
export interface Dashboard {
  id: string;
  title: string;
  account_id: string;
  user_id: string;
  creator: string;
  created_at_date: Date;
  modified_at_date: Date;
  user_modified_at_date: Date;
  last_accessed_date: Date;
  width: number;
  height: number;
  locked: boolean;
  payload: DashboardPayload;
  properties: {
    is_locked: boolean;
  };
  widgets: Widget[];
  dashboard_version?: number;
  project_id?: string;
  widgetGroups: { [key: string]: WidgetGroup };
  comparisons: { [key: string]: Comparison };
  visibility: DashboardVisibility;
  revision?: number;
  objectLabels?: string[] | ObjectLabel[];
}

export interface WidgetTitleOptions {
  skipVolumeVsSentimentSelectedItem?: boolean;
  selectedItemName: string;
}

export interface SectionFilter {
  field: string;
  buckets: string[];
  type?: SectionMetadataType;
}

export class WidgetUtility {
  public static getWidgetTitle(
    widget: Widget<any>,
    options: WidgetTitleOptions = { selectedItemName: widget.payload.selectedItems[0] }
  ) {
    if (widget.title) {
      return widget.title;
    }

    let itemName: string = widget.payload.analyticItem || '';
    itemName = FieldService.getFieldLongName(itemName);

    if (itemName) {
      if (
        !widget.payload.customFieldSelected &&
        !widget.payload.standardFieldSelected &&
        widget.payload.analyticItem !== widget.payload.entityTypeFilter
      ) {
        itemName = translate(`analytic-items.${itemName.toLowerCase()}`);
      }

      if (
        (widget.payload.analyticItem === 'entities' || widget.payload.analyticItem === 'topics') &&
        widget.payload.entityTypeFilter
      ) {
        const subTypes = widget.payload.entityTypeFilter.split(',').map((subItem: string) => {
          if (widget.payload.analyticItem === 'topics') {
            return translate(`analytic-items.${subItem.toLowerCase()}`);
          } else {
            return subItem;
          }
        });

        const singularItemName =
          widget.payload.analyticItem === 'entities'
            ? translate('analytic-items.entity')
            : translate('analytic-items.topic');
        itemName = `${singularItemName}: ${subTypes.join(', ')}`;
      }
    }

    switch (widget.payload.type) {
      case 'documentCount':
        itemName += translate('widgets.title.document-count');
        break;
      case 'sentimentPolarity':
        itemName += translate('widgets.title.sentiment-polarity');
        break;
      case 'sentimentScore':
        itemName += translate('widgets.title.sentiment-score');
        break;
      case 'wordcloud':
        break;
      case 'sentimentVolume':
        itemName += translate('widgets.title.sentiment-vs-volume');
        if (options.selectedItemName && !options.skipVolumeVsSentimentSelectedItem) {
          itemName += ': ' + FieldService.getFieldLongName(options.selectedItemName);
        }
        break;
      case 'taxonomyMap':
        itemName = translate('widgets.title.taxonomy');
        break;
      case 'crosstab':
        itemName = translate('widgets.title.crosstab');
        break;
      case 'featureScoring':
        itemName = translate('widgets.title.topic-scoring');
        break;
      case 'sentimentAccuracy':
        itemName = translate('widgets.title.accuracy');
        break;
      case 'numericMetadata':
        itemName = translate('widgets.title.numeric-metadata-over-time');
        break;
      case 'scatterPlot':
        itemName += ` ${ScatterPlotUtils.getAxisName(
          widget.payload.xAxis,
          true
        )} vs ${ScatterPlotUtils.getAxisName(widget.payload.yAxis, true)}`;
        break;
      case 'impact':
        itemName += translate('widgets.title.impact', { order: widget.payload.sort.order });
        break;
      case 'breakdown':
        itemName = translate('widgets.title.breakdown');
        break;
    }
    return `${itemName}`;
  }
}

export interface ShareRequestModel {
  expireAfterMinutes: number;
}

export interface ShareResponseModel {
  accessToken: string;
}

export type DashboardSort = 'last_accessed_date' | 'modified_at' | 'title';
export type DashboardSortOrder = 'asc' | 'desc';
export type DashboardFilter = 'self' | 'recent';

export const DASHBOARD_VERSION = 19;
export const DASHBOARD_WIDTH = 12;

@EnableClassTelemetry()
@Injectable()
export class DashboardService extends BridgeAPIService {
  static map(dashboard: Dashboard, labels?: ObjectLabel[]): Dashboard {
    dashboard.created_at_date = new Date(dashboard.created_at_date);
    dashboard.modified_at_date = new Date(dashboard.modified_at_date);
    dashboard.user_modified_at_date = new Date(dashboard.user_modified_at_date);
    dashboard.objectLabels = this.mapObjectLabels(dashboard, labels);

    return dashboard;
  }

  constructor(
    public http: HttpClient,
    public systemService: SystemService,
    private groupService: GroupService,
    private realtimeService: RealtimeService,
    private router: Router
  ) {
    super(http, systemService);
  }

  public on(namespace: string, fn: RealtimeEventHandler<Dashboard>): RealtimeConnection {
    return this.realtimeService.on(
      namespace,
      {
        dashboard: fn,
      },
      'dashboardService'
    );
  }

  public createDashboard(
    dashboard: Partial<Dashboard>,
    projectId?: string,
    dashboardVersion = DASHBOARD_VERSION
  ): Promise<Dashboard> {
    dashboard.dashboard_version = dashboardVersion;
    dashboard.width = DASHBOARD_WIDTH;
    delete dashboard.last_accessed_date;
    delete dashboard.properties;
    delete dashboard.project_id;
    delete dashboard.id;

    return this.post<Dashboard>(urlFor(`/dashboards`, projectId), dashboard);
  }

  public createCachedDashboard(dashboard: Dashboard, userId: string): Dashboard {
    dashboard.id = 'new';
    dashboard.dashboard_version = DASHBOARD_VERSION;
    dashboard.width = DASHBOARD_WIDTH;

    this.storeCachedDashboard(dashboard, userId);

    return dashboard;
  }

  public storeCachedDashboard(dashboard: Partial<Dashboard>, userId: string) {
    this.localStorage.setItem(
      this.getDashboardCacheKey(dashboard.id, dashboard.project_id, userId),
      dashboard,
      'local',
      false,
      (_, v) => (v === undefined ? null : v)
    );
  }

  public loadCachedDashboard(dashboardId: string, projectId: string, userId: string): Dashboard {
    const dashboardItem = this.localStorage.getItem(
      this.getDashboardCacheKey(dashboardId, projectId, userId)
    );

    if (dashboardItem) {
      return dashboardItem;
    }
  }

  public removeCachedDashboard(dashboardId: string, projectId: string, userId: string) {
    this.localStorage.removeItem(this.getDashboardCacheKey(dashboardId, projectId, userId));
  }

  public getDashboards(
    projectId?: string,
    sort: DashboardSort = 'title',
    sortOrder: DashboardSortOrder = 'asc'
  ): Promise<BridgeListResult<Dashboard>> {
    return this.searchDashboards(undefined, undefined, sort, sortOrder, 10000, projectId);
  }

  public searchDashboards(
    query?: string,
    filter?: DashboardFilter,
    sort: DashboardSort = 'title',
    sortOrder: DashboardSortOrder = 'asc',
    limit: number = 20,
    projectId?: string
  ): Promise<BridgeListResult<Dashboard>> {
    const combinedSort = sort + ':' + sortOrder;
    return this.getPage<Dashboard>(urlFor('/dashboards', projectId), {
      query: query || null,
      filter,
      sort: combinedSort,
      limit,
      dashboard_version: `>=2`,
    });
  }

  public getDashboardRevisions(
    projectId: string,
    dashboardId: string
  ): Promise<BridgeListResult<Dashboard>> {
    return this.get(urlFor(`/dashboards/${dashboardId}/revisions`, projectId), { limit: 10 });
  }

  public restoreDashboardRevision(
    projectId: string,
    dashboardId: string,
    revision: number
  ): Promise<Dashboard> {
    return this.post(urlFor(`/dashboards/${dashboardId}/_restore/${revision}`, projectId));
  }

  public async shareDashboard(dashboard: Dashboard, options: ShareRequestModel): Promise<string> {
    const response = await this.post<ShareResponseModel>(
      urlFor(`/dashboards/${dashboard.id}/_share`, dashboard.project_id),
      options
    );
    return response.accessToken;
  }

  public markAccessed(dashboard: Dashboard): Promise<void> {
    dashboard.last_accessed_date = new Date();
    return this.post<void>(
      urlFor(`/dashboards/${dashboard.id}/_mark_accessed`, dashboard.project_id),
      {}
    );
  }

  public lockDashboard(dashboard: Dashboard): Promise<Dashboard> {
    return this.post<Dashboard>(
      urlFor(`/dashboards/${dashboard.id}/_lock`, dashboard.project_id),
      {}
    );
  }

  public unlockDashboard(dashboard: Dashboard): Promise<Dashboard> {
    return this.post<Dashboard>(
      urlFor(`/dashboards/${dashboard.id}/_unlock`, dashboard.project_id),
      {}
    );
  }

  public updateDashboard(
    dashboard: Dashboard,
    checkConflict = true,
    isUserModification = true
  ): Promise<Dashboard> {
    if (dashboard.project_id) {
      dashboard.widgets.forEach((widget) => {
        if (widget.payload.analysisId) {
          delete widget.payload.analysisId;
        }
      });
    }

    const { ...dashboardData } = dashboard;

    return this.put<Dashboard>(
      urlFor(
        `/dashboards/${dashboard.id}?checkConflict=${checkConflict}&isUserModification=${isUserModification}`,
        dashboard.project_id
      ),
      dashboardData
    );
  }

  public getDashboardById(
    dashboardId: string,
    projectId?: string,
    sideloads?: 'objectLabels'
  ): Promise<Dashboard> {
    const params = sideloads ? { sideloads } : {};

    return this.get<Sideloadable<Dashboard>>(
      urlFor(`/dashboards/${dashboardId}`, projectId),
      params
    ).then((dashboard) => DashboardService.map(dashboard, dashboard.sideloaded?.objectLabels));
  }

  public deleteDashboard(dashboardId: string, projectId?: string): Promise<void> {
    return this.delete<void>(urlFor(`/dashboards/${dashboardId}`, projectId));
  }

  public cloneDashboard(dashboardId: string, projectId?: string): Promise<Dashboard> {
    return this.post<Dashboard>(urlFor(`/dashboards/${dashboardId}/clone`, projectId));
  }

  public exportDashboard(dashboard: Dashboard): void {
    const filename = dashboard.title.replace(new RegExp('\\s', 'g'), '-') + '-export.json';

    const file = new File([JSON.stringify(dashboard, null, '  ')], filename, {
      type: 'text/plain; charset=utf-8',
    });

    this.systemService.saveFile(file);
  }

  public async gotoDashboard(dashboard: Dashboard): Promise<boolean> {
    return this.router.navigate([
      `groups/${this.groupService.getSelectedGroupId(true)}/projects/${
        dashboard.project_id
      }/dashboards/${dashboard.id}`,
    ]);
  }

  validateDashboardName(name, projectId?): Promise<Dashboard | undefined> {
    return this.get<Dashboard>(urlFor(`/dashboards/_validate_name`, projectId), { name });
  }

  @DisableMethodTelemetry()
  private getDashboardCacheKey(dashboardId, projectId, userId) {
    return `dashboard_cache-${dashboardId}-${projectId}-${userId}`;
  }
}
